import "./styles.css";
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import createGlobe from "cobe";
import usePartySocket from "partysocket/react";

import type { OutgoingMessage } from "../shared";

const getMarkerSize = (count: number) => {
  return Math.min(0.11, 0.035 + Math.log1p(count) * 0.012);
};

function App() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);

  const [counter, setCounter] = useState(0);

  const positions = useRef<
    Map<string, { location: [number, number]; size: number }>
  >(new Map());

  const sizeRef = useRef({ size: 0, dpr: 1 });

  usePartySocket({
    room: "global",
    party: "globe",
    onMessage(evt) {
      const message = JSON.parse(evt.data as string) as OutgoingMessage;

      if (message.type === "recent-snapshot") {
        positions.current = new Map(
          message.positions.map((position) => [
            position.id,
            {
              location: [position.lat, position.lng],
              size: getMarkerSize(position.count),
            },
          ]),
        );
        setCounter(message.count);
      } else if (message.type === "add-marker") {
        positions.current.set(message.position.id, {
          location: [message.position.lat, message.position.lng],
          size: 0.06,
        });
      } else if (message.type === "remove-marker") {
        positions.current.delete(message.id);
      } else if (message.type === "viewer-count") {
        setCounter(message.count);
      }
    },
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;

    const computeSize = () => {
      // 关键：以“正方形舞台”的宽度为准（因为你 iframe 外面是 1:1 容器）
      const size = stage.clientWidth;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      sizeRef.current = { size, dpr };

      canvas.width = Math.floor(size * dpr);
      canvas.height = Math.floor(size * dpr);
    };

    computeSize();

    let phi = 0;
    let last = performance.now();

    const globe = createGlobe(canvas, {
      devicePixelRatio: sizeRef.current.dpr,
      width: sizeRef.current.size * sizeRef.current.dpr,
      height: sizeRef.current.size * sizeRef.current.dpr,

      phi: 0,
      theta: 0.1,
      dark: 1,
      diffuse: 0.8,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [0.3, 0.3, 0.3],
      markerColor: [0.9, 0.15, 0.15],
      glowColor: [0.2, 0.2, 0.2],
      opacity: 0.85,
      markers: [],

      onRender: (state) => {
        const { size, dpr } = sizeRef.current;
        state.devicePixelRatio = dpr;
        state.width = size * dpr;
        state.height = size * dpr;

        state.markers = [...positions.current.values()];

        const now = performance.now();
        const dt = now - last;
        last = now;

        state.phi = phi;
        phi += dt * 0.0005;
      },
    });

    const onResize = () => computeSize();
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      globe.destroy();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    // 这个页面作为 iframe 内容：直接撑满，不用 100vh
    <div
      style={{
        width: "100%",
        height: "100%",
        background: "#111",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* 正方形舞台：永远 1:1 */}
      <div
        ref={stageRef}
        style={{
          width: "100%",
          maxWidth: "600px",
          aspectRatio: "1 / 1",
          position: "relative",
        }}
      >
        {/* Canvas 填满舞台；往上微调：让地球看起来更靠上 */}
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            display: "block",
            outline: "none",
            transform: "translateY(-14px)", // ✅ 地球偏下：调这个（-8 ~ -24 都可以）
          }}
        />

        {/* 文案叠在舞台底部：不会被 iframe 正方形裁掉 */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "10px",
            textAlign: "center",
            fontSize: "13px",
            color: "rgba(255,255,255,0.72)",
            pointerEvents: "none",
            textShadow: "0 2px 10px rgba(0,0,0,0.5)",
          }}
        >
          现在有{" "}
          <b style={{ color: "#22c55e", fontWeight: 600 }}>
            {Math.max(0, counter)}
          </b>{" "}
          个陌生人正在看
        </div>
      </div>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
